import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { resolveLocale, getUIDirection } from '@fluidx/utilities';
import { MenuViewProps, MenuView, MenuRenderProps } from '@fluidx/office-fluid-types';
import { MenuProps, Menu } from './Menu';

let currentTarget: HTMLElement | undefined;

export async function getMenuRenderer(
  rendererProps: MenuViewProps,
  cssProperties: () => { [propertyName: string]: string } | undefined
): Promise<MenuView | undefined> {
  const { coordinates, ownerDocument, locale } = rendererProps;

  if (coordinates === undefined || ownerDocument === undefined) {
    return undefined;
  }

  // Menu should be shown above trigger event if there is not enough space to render menu below the coordinates
  const flip = rendererProps.flip || window.innerHeight - coordinates.y < 250; /* height of menu */

  // Create container element positioned at event.x, event.y
  if (currentTarget === undefined) {
    const resolvedCoordinates = { x: coordinates.x, y: coordinates.y };
    if (flip && rendererProps.coordinatesFlip) {
      resolvedCoordinates.x = rendererProps.coordinatesFlip.x;
      resolvedCoordinates.y = rendererProps.coordinatesFlip.y;
    }
    currentTarget = createContainerElement(resolvedCoordinates, ownerDocument);
  }

  const resolvedLocale = resolveLocale(locale);

  return {
    async render(renderProps: MenuRenderProps) {
      if (currentTarget === undefined) {
        return;
      }

      const menuProps: MenuProps = {
        ...rendererProps,
        ...renderProps,
        target: currentTarget,
        flip,
        cssProperties,
        direction: getUIDirection(resolvedLocale)
      };
      ReactDOM.render(<Menu {...menuProps} />, currentTarget);
    },
    clear() {
      if (currentTarget !== undefined) {
        ReactDOM.unmountComponentAtNode(currentTarget);
        currentTarget.remove();
        currentTarget = undefined;
      }
    }
  };
}

function createContainerElement(position: { x: number; y: number }, ownerDocument: Document): HTMLElement {
  const containerElement = ownerDocument.createElement('div');

  // Position the div where the menu was invoked.
  containerElement.style.position = 'absolute';
  containerElement.style.left = position.x + 'px';
  containerElement.style.top = position.y + 'px';

  ownerDocument.body.appendChild(containerElement);

  return containerElement;
}

// cspell:ignore unmount
